/**
 * Copyright (c) 2025-2026 Four Hour Window LLC
 * All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import React from 'react';
import { formatCurrency, formatDate, formatDateOnly } from '../utils/formatters.ts';
import { PayoutOrder } from '../services/payoutService.ts';

interface PayoutRowExpandedProps {
  payout: {
    id: string;
    userId: string;
    userEmail: string;
    collectionTitle: string;
    collectionHandle?: string;
    orders: PayoutOrder[];
    date: string;
    amount: number;
    method: string;
    confirmationNumber: string;
    notes: string;
    createdAt: string;
    createdBy: string;
    status: string;
    updatedBy?: string;
    updatedAt?: string;
  };
}

const PayoutRowExpanded: React.FC<PayoutRowExpandedProps> = ({ payout }) => {
  // Calculate total from individual orders (if amounts are present)
  const calculatedTotal = payout.orders.reduce((sum, order) => {
    return sum + (order.amount || 0);
  }, 0);
  
  const hasIndividualAmounts = payout.orders.some(order => 
    order.amount !== undefined && order.amount !== null && order.amount !== 0
  );

  return (
    <div className="payout-details-expanded">
      <div className="details-grid">
        <div className="detail-section">
          <h4 className="m-t-0">Collection:</h4>
          <span className="info-value">{payout.collectionTitle}</span>
        </div>
        
        <div className="detail-section">
          <h4>Order Details ({payout.orders.length} {payout.orders.length === 1 ? 'order' : 'orders'})</h4>
          <div className="orders-expanded-list">
            {payout.orders.map((order, idx) => (
              <div key={idx} className="order-item-expanded" style={{
                padding: '5px 8px'
              }}>
                <div className="order-row">
                  <div>
                    <span className="order-number" style={{ fontWeight: 'bold' }}>
                      #{order.number}
                    </span>
                    {' '}
                    <span className="order-id" style={{ color: '#6c757d', fontSize: '0.9em' }}>
                      ({order.id})
                    </span>
                  </div>
                  {order.amount !== undefined && order.amount !== null && order.amount !== 0 && (
                    <span className="green" style={{ 
                  
                      fontWeight: 'bold',
                      fontSize: '0.95em'
                    }}>
                      {formatCurrency(order.amount)}
                    </span>
                  )}
                </div>
                
              </div>
            ))}
          </div>

        </div>

        <div className="detail-section">
          <h4>Payment Information</h4>
          <div className="info-rows">
            <div className="info-row">
              <span className="info-label" >Total Amount:</span>
              <span className="info-value" style={{ color: 'rgb(121 155 127)', fontWeight: 'bold' }}>
                {formatCurrency(payout.amount)}
              </span>
            </div>
            {hasIndividualAmounts && Math.abs(calculatedTotal - payout.amount) > 0.01 && (
              <div className="info-row" style={{ 
                backgroundColor: '#fff3cd', 
                padding: '4px 8px', 
                borderRadius: '4px',
                fontSize: '0.85em'
              }}>
                <span className="info-label">⚠️ Note:</span>
                <span className="info-value" style={{ fontStyle: 'italic' }}>
                  Total differs from sum of orders by {formatCurrency(Math.abs(payout.amount - calculatedTotal))}
                </span>
              </div>
            )}
            <div className="info-row">
              <span className="info-label">Payment Date:</span>
              <span className="info-value">{formatDateOnly(payout.date)}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Method:</span>
              <span className="info-value">{payout.method}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Confirmation #:</span>
              <span className="info-value">{payout.confirmationNumber}</span>
            </div>
            {payout.notes && (
              <div className="info-row">
                <span className="info-label">Notes:</span>
                <span className="info-value" style={{ fontStyle: 'italic' }}>
                  {payout.notes}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="detail-section">
          <h4>Audit Trail</h4>
          <div className="info-rows">
            <div className="info-row">
              <span className="info-label">Created By:</span>
              <span className="info-value">{payout.createdBy}</span>
            </div>
            {/* <div className="info-row">
              <span className="info-label">User Email:</span>
              <span className="info-value">{payout.userEmail}</span>
            </div> */}
            <div className="info-row">
              <span className="info-label">Created At:</span>
              <span className="info-value">{formatDate(payout.createdAt)}</span>
            </div>
            {payout.updatedBy && (
              <>
                <div className="info-row">
                  <span className="info-label">Updated By:</span>
                  <span className="info-value">{payout.updatedBy}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Updated At:</span>
                  <span className="info-value">{formatDate(payout.updatedAt)}</span>
                </div>
              </>
            )}
            <div className="info-row">
              <span className="info-label">Status:</span>
              <span className="info-value">
                <span className="status-badge">{payout.status}</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PayoutRowExpanded;